import { PermIdentity as PermIdentityIcon } from "@mui/icons-material";
import { Autocomplete, Chip, ListItem, ListItemIcon, ListItemText, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { truncate as _truncate } from "lodash";

import { CloseWhite, TagIcon } from "../../icons";
import renderTypeIcon from "../../utils/renderTypeIcon";

const truncate = (string) => _truncate(string, { length: 20 });

const useStyles = makeStyles((theme) => ({
  chip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    borderRadius: "20px",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.87)",
    },
  },
  chipIcon: {
    padding: "5px",
    margin: "4px 4px 4px 4px",
  },
  chipLabel: {
    padding: "0 8px 0 8px",
  },
  autocompleteRoot: {
    "& .MuiFilledInput-root": {
      padding: "5px 15px !important",
    },
  },
  paper: {
    boxShadow: "none",
    margin: 0,
    color: theme.palette.common.white,
    fontSize: "16px",
    backgroundColor: "#2E2E2E",
    borderRadius: "0",
  },
  option: {
    height: "40px",
    alignItems: "flex-start",
    opacity: 0.5,
    padding: 0,

    '&[aria-selected="true"]': {
      color: theme.palette.common.white,
      opacity: 1,
    },
    '&[data-focus="true"]': {
      color: theme.palette.common.white,
      opacity: 1,
    },
    "&:hover": {
      color: theme.palette.common.white,
      opacity: 1,
    },
  },
  popperDisablePortal: {
    position: "relative",
  },
  noOptions: {
    color: theme.palette.common.white,
    opacity: ".5",
  },
  listbox: {
    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "10px 0px",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0, 0, 0, 0.2)",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "& .MuiAutocomplete-option": {
      padding: "0 10px",
    },
  },
}));

function FormAutoComplete(props) {
  const {
    inputValue,
    multiple = true,
    onChange,
    onInputChange,
    options,
    renderInput,
    renderTags,
    testId,
    value,
    locked,
    isInCustomizeMode,
  } = props;

  const classes = useStyles();
  const theme = useTheme();

  const getOptionLabel = (option) => {
    if (!option) {
      return "";
    }

    if (typeof option === "string") {
      return truncate(option);
    }

    return truncate(option.name) || truncate(option.value) || "";
  };

  const getOptionIcon = (icon) => {
    if (icon === "tag") {
      return <TagIcon fontSize="small" style={{ fontSize: ".9em" }} />;
    } else if (icon === "user") {
      return <PermIdentityIcon style={{ fontSize: "1.1em" }} color="currentColor" />;
    } else {
      return renderTypeIcon(icon, { style: { fontSize: ".9em", fill: "none" }, color: theme.palette.primary.main });
    }
  };

  return (
    <Autocomplete
      freeSolo
      multiple={multiple}
      autoComplete
      autoHighlight
      filterSelectedOptions
      onChange={onChange}
      value={value}
      onInputChange={onInputChange}
      inputValue={inputValue}
      options={options}
      readOnly={locked && isInCustomizeMode}
      getOptionLabel={(option = {}) => getOptionLabel(option)}
      classes={{
        root: classes.autocompleteRoot,
        paper: classes.paper,
        option: classes.option,
        listbox: classes.listbox,
        popperDisablePortal: classes.popperDisablePortal,
        noOptions: classes.noOptions,
        inputRoot: classes.autocompleteInputRoot,
      }}
      data-testid={testId}
      renderTags={
        renderTags ||
        ((value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id || option.value}
              label={getOptionLabel(option)}
              value={option.value}
              classes={{
                root: classes.chip,
                label: classes.chipLabel,
                deleteIcon: classes.chipIcon,
              }}
              deleteIcon={<CloseWhite />}
            />
          )))
      }
      renderOption={(renderProps, option) => (
        <ListItem {...renderProps} key={option.value}>
          {option.icon && (
            <ListItemIcon sx={{ minWidth: "auto" }} style={{ marginRight: "12px" }}>
              {getOptionIcon(option.icon)}
            </ListItemIcon>
          )}
          <ListItemText primary={getOptionLabel(option)} />
        </ListItem>
      )}
      renderInput={renderInput}
    />
  );
}

export default FormAutoComplete;
